import { NetworkState, NetworkMetadata, NetworkInformation } from '../types/network';
import { getNetworkState } from '../utils/getNetworkState';

/**
 * Singleton service responsible for maintaining current network state
 * and exposing it for baseMetadata usage.
 *
 * Features:
 * - Debounces rapid network changes (default 5 seconds)
 * - Handles online/offline events
 * - Monitors navigator.connection changes (Chrome/Android)
 * - Handles visibility change (resume detection)
 * - Never emits standalone analytics events
 * - Always returns synchronously
 */
class NetworkMetadataService {
  private currentState: NetworkState = { online: false, lastUpdated: 0 };
  private lastUpdatedTimestamp = 0;
  private debounceTimer?: NodeJS.Timeout;
  private readonly debounceMs: number = 5000; // 5 seconds
  private isInitialized = false;
  private connectionChangeHandler?: () => void;
  private onlineHandler?: () => void;
  private offlineHandler?: () => void;
  private visibilityChangeHandler?: () => void;

  /**
   * Initialize the service and set up event listeners.
   * Safe to call multiple times - will only initialize once.
   */
  init(): void {
    if (this.isInitialized) {
      return;
    }

    // Get initial state
    this.updateState(getNetworkState());
    this.isInitialized = true;

    // Set up event listeners
    this.setupEventListeners();
  }

  /**
   * Get the current network metadata for baseMetadata injection.
   * Always returns synchronously - never async.
   * Auto-initializes the service on first call if not already initialized.
   *
   * @returns NetworkMetadata object (may have default values if not yet initialized)
   */
  getNetworkMetadata(): NetworkMetadata {
    // Auto-initialize on first use if not already initialized
    if (!this.isInitialized) {
      this.init();
    }

    return {
      online: this.currentState.online,
      type: this.currentState.type,
      effectiveType: this.currentState.effectiveType,
      downlink: this.currentState.downlink,
      rtt: this.currentState.rtt,
      lastUpdated: this.currentState.lastUpdated,
    };
  }

  /**
   * Handle visibility change events (tab resume, app foreground).
   * Re-evaluates network state when the page becomes visible.
   */
  handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      // Page became visible - check network state
      this.updateStateDebounced(getNetworkState());
    }
  }

  /**
   * Clean up all event listeners and timers.
   * Should be called when the service is no longer needed.
   */
  cleanup(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
    }

    this.removeEventListeners();
    this.isInitialized = false;
    this.currentState = { online: false, lastUpdated: 0 };
  }

  /**
   * Update network state immediately (no debounce).
   * Used for initial state and critical changes.
   */
  private updateState(newState: NetworkState): void {
    const hasChanged = this.hasSignificantChange(newState);

    if (hasChanged) {
      this.currentState = newState;
      this.lastUpdatedTimestamp = newState.lastUpdated;
    }
  }

  /**
   * Update network state with debouncing.
   * Used for connection change events that may fire rapidly.
   */
  private updateStateDebounced(newState: NetworkState): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.updateState(newState);
      this.debounceTimer = undefined;
    }, this.debounceMs);
  }

  /**
   * Check if the new state represents a meaningful change.
   * Only updates on significant changes to avoid unnecessary state updates.
   */
  private hasSignificantChange(newState: NetworkState): boolean {
    const current = this.currentState;

    // Online/offline changes are always significant
    if (current.online !== newState.online) {
      return true;
    }

    // Connection type changes are significant
    if (current.type !== newState.type) {
      return true;
    }

    // Effective type changes are significant
    if (current.effectiveType !== newState.effectiveType) {
      return true;
    }

    // Downlink changes of more than 0.5 Mbps are significant
    if (
      current.downlink != null &&
      newState.downlink != null &&
      Math.abs(current.downlink - newState.downlink) > 0.5
    ) {
      return true;
    }

    // RTT changes of more than 50ms are significant
    if (current.rtt != null && newState.rtt != null && Math.abs(current.rtt - newState.rtt) > 50) {
      return true;
    }

    return false;
  }

  /**
   * Set up all browser event listeners for network changes.
   */
  private setupEventListeners(): void {
    // Online/offline events
    this.onlineHandler = () => {
      this.updateState(getNetworkState());
    };
    this.offlineHandler = () => {
      this.updateState(getNetworkState());
    };

    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    // Visibility change (resume detection)
    this.visibilityChangeHandler = () => {
      this.handleVisibilityChange();
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // navigator.connection change event (Chrome/Android only)
    const { connection } = navigator as { connection?: NetworkInformation };
    if (connection && 'addEventListener' in connection) {
      this.connectionChangeHandler = () => {
        this.updateStateDebounced(getNetworkState());
      };
      connection.addEventListener('change', this.connectionChangeHandler);
    }
  }

  /**
   * Remove all event listeners.
   */
  private removeEventListeners(): void {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = undefined;
    }

    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
      this.offlineHandler = undefined;
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }

    if (this.connectionChangeHandler) {
      const { connection } = navigator as { connection?: NetworkInformation };
      if (connection && 'removeEventListener' in connection) {
        connection.removeEventListener('change', this.connectionChangeHandler);
      }
      this.connectionChangeHandler = undefined;
    }
  }
}

// Export singleton instance
export const networkMetadataService = new NetworkMetadataService();
